import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";

import "./MemberGameDetails.scss";
import { toast } from "react-toastify";
import areanaImg from "../../../../../assets/areanaDefault.png";
import UserContext from "../../../../../context/UserContext";
import Loader from "../../../../../components/Loader/Loader";

function MemberGameDetails() {
  const { booking_id } = useParams();
  const { userDetail } = useContext(UserContext); // Access user details from context
  const [facility, setFacility] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false); // State for modal visibility
  const [isUserInGame, setIsUserInGame] = useState(false); // State to track if user is in the game
  const navigate = useNavigate();

  // Fetch facility details
  const fetchFacilityDetails = async () => {
    setLoading(true);
    setError(null);

    const url = `https://ballersapi.ballerslife.co/api/v1/memberships/bookings/get_joining_game_details?booking_id=${booking_id}`;
    const accessToken = window.localStorage.getItem("access_token");
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: "",
      });

      const data = await response.json();
      if (response.status === 200) {
        setFacility(data);
        // Check if the current user is in the players_joined list
        // console.log("User Detail:", userDetail);
        if (userDetail?.member_name) {
          const isInGame = data.players_joined.some((player) => {
            const playerName = player.name.replace(/\s*\(host\)$/, ""); // Remove " (host)" if present
            return playerName === userDetail.member_name;
          });
          setIsUserInGame(isInGame);
        }
      } else {
        setError("Failed to fetch facility details. Please try again.");
        toast.error(data.detail || "Failed to fetch facility details.");
      }
    } catch (err) {
      // console.log(err);
      setError("An error occurred while fetching facility details.");
      toast.error("An error occurred while fetching facility details.");
    } finally {
      setLoading(false);
    }
  };

  // Handle joining the game
  const handleJoinGame = async () => {
    const url = `https://ballersapi.ballerslife.co/api/v1/memberships/bookings/join_game?booking_id=${booking_id}`;
    const accessToken = window.localStorage.getItem("access_token");
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: "",
      });

      const data = await response.json();
      if (response.status === 200) {
        setShowModal(true); // Show the modal on success
        fetchFacilityDetails(); // Refresh the game details
      } else {
        toast.error(data.detail || "Failed to join the game.");
      }
    } catch (err) {
      // console.log(err);
      // toast.error("An error occurred while joining the game.");
    }
  };

  // Handle leaving the game
  const handleLeaveGame = async () => {
    const url = `https://ballersapi.ballerslife.co/api/v1/memberships/bookings/leave_game?booking_id=${booking_id}`;
    const accessToken = window.localStorage.getItem("access_token");

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: "",
      });
      const data = await response.json();

      if (response.status === 200) {
        toast.success(data.message);
        fetchFacilityDetails(); // Refresh the game details
        setIsUserInGame(false); // Update the state
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error("An error occurred while leaving the game.");
    }
  };

  const handleCancelGame = async () => {
    const url = `https://ballersapi.ballerslife.co/api/v1/memberships/cancel_booking?booking_id=${booking_id}`;
    const accessToken = window.localStorage.getItem("access_token");
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: "", // required for the API even if empty
      });
      const data = await response.json();
      if (response.status == 200) {
        toast.success(data.message);
        fetchFacilityDetails(); // Refresh the game details
        navigate("/dashboard/reserve");
      } else {
        toast.error(data.message);
        // toast.error(data.detail || "Failed to cancel the booking.");
      }
    } catch (err) {
      toast.error("An error occurred while cancelling the booking.");
    }
  };

  useEffect(() => {
    fetchFacilityDetails();
  }, [booking_id]);

  if (loading) return <Loader />;
  if (error) return <p>{error}</p>;
  if (!facility) return <p>No facility details found.</p>;

  // Prepare available sports for display
  const availableSports = [facility.sport];

  // Prepare player list for display
  const playerList = facility.players_joined.map((player) => ({
    id: player.user_id,
    name: player.name,
    is_host: player.name.includes("(host)"),
  }));

  return (
    <>
      <div className="goBackSvgOppor">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="21"
          height="21"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#FFBF00"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="lucide lucide-chevron-left"
          onClick={() => {
            navigate("/dashboard/joingame");
          }}
        >
          <path d="m15 18-6-6 6-6" />
        </svg>
      </div>
      <div className="facility-details">
        {/* Facility Info (Banner) */}
        <div className="facility-info">
          <img
            src={facility.facility_image || areanaImg}
            alt={facility.facility_name}
            className="facility-image"
          />
          <div className="facility-text">
            <h2 className="facility-name">{facility.facility_name}</h2>
            <p className="facility-location">
              {facility.locality}, {facility.city}
              <a
                href={facility.direction_link}
                target="_blank"
                rel="noopener noreferrer"
                className="maps-link"
              >
                <span className="maps-icon"></span>
              </a>
            </p>
            <p className="facility-sports">
              {availableSports
                .map((sport) => sport.charAt(0).toUpperCase() + sport.slice(1))
                .join(", ")}
            </p>
          </div>
        </div>

        {/* Game Details and Players Section */}
        <div className="game-details-container">
          <div className="game-details">
            <h3 className="section-title">Game Details</h3>
            <div className="game-details-list">
              <div>
                <div className="detail-item">
                  <span className="icon target-icon"></span>
                  <p>
                    {facility.sport.charAt(0).toUpperCase() +
                      facility.sport.slice(1)}
                  </p>
                </div>

                <div className="detail-item">
                  <span className="icon clock-icon"></span>
                  <p>{facility.booking_time}</p>
                </div>
              </div>
              <div>
                <div className="detail-item">
                  <span className="icon calendar-icon"></span>
                  <p>{facility.booking_date}</p>
                </div>
                <div className="detail-item">
                  <span className="icon players-icon"></span>
                  <p>{`${facility.no_players_joined}/${facility.players_req}`}</p>
                </div>
              </div>
            </div>
            {console.log(facility.status)}
            {facility?.status == "pending" && (
              <div className="detail-item bookingStatusIcon">
                <span className="icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="19"
                    height="19"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-circle-alert-icon lucide-circle-alert"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" x2="12" y1="8" y2="12" />
                    <line x1="12" x2="12.01" y1="16" y2="16" />
                  </svg>
                </span>
                <p
                  style={{
                    color: "#ffca2b",
                  }}
                >
                  Booking Pending{" "}
                </p>
              </div>
            )}
            {facility?.status == "confirmed" && (
              <div className="detail-item bookingStatusIcon">
                <span className="icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="19"
                    height="19"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-check-icon lucide-check"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                </span>
                <p
                  style={{
                    color: "green",
                  }}
                >
                  Booking Confirmed{" "}
                </p>
              </div>
            )}
          </div>

          <div className="players">
            <h3 className="section-title">Players</h3>
            <div className="player-list">
              {playerList.map((player) => (
                <div key={player.id} className="player-item">
                  <span className="player-placeholder"></span>
                  <p>
                    {player.name} {player.is_host}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="game-details-container">
          <div className="game-details">
            <h3 className="section-title-note">Game Note :</h3>
            <p className="noteText">
              • Scan QR code (on homepage) at venue to Check-in.
            </p>
            <p className="noteText">
              • Checkin Window: 15 mins before to 15 mins after the game starts.
            </p>
            <p className="noteText">• Miss it = No Show = 4 days cut</p>
          </div>
        </div>

        {/* Join/Leave Game Button */}
        <div className="join-game-container">
          {facility.booking_notes && (
            <span className="join-game-bookingNotes">
              * {facility.booking_notes}
            </span>
          )}
          {facility.status === "cancelled" ? (
            <button className="join-game-btn" disabled>
              Cancelled
            </button>
          ) : (
            <>
              {facility.booking_active ? (
                <>
                  {facility.host ? (
                    <button
                      className="join-game-btn"
                      onClick={handleCancelGame}
                    >
                      Cancel the Game
                    </button>
                  ) : facility.full_house && !facility.joined_game ? (
                    <button className="join-game-btn" disabled>
                      House Full
                    </button>
                  ) : facility.joined_game ? (
                    <button className="join-game-btn" onClick={handleLeaveGame}>
                      Leave the Game
                    </button>
                  ) : (
                    <button className="join-game-btn" onClick={handleJoinGame}>
                      Join the Game
                    </button>
                  )}
                </>
              ) : (
                <></> // No button shown
              )}
            </>
          )}
        </div>

        {/* Confirmation Modal */}
        {showModal && (
          <div className="modal-overlay">
            <div className="confirmation-modal">
              <p className="modal-title1">
                You're in! Game reserved successfully.
              </p>
              <p className="modal-submessage">
                Note: The game will be confirmed only when all players join.
              </p>
              <div className="confirmation-details">
                <ul className="confirmation-list">
                  <li>
                    <span
                      style={{
                        fontSize: "1.2rem",
                      }}
                    >
                      📆
                    </span>
                    You can cancel up to 12 hours before the game — no penalty.
                  </li>
                  <li>
                    <span
                      style={{
                        fontSize: "1.2rem",
                      }}
                    >
                      🚫
                    </span>{" "}
                    Cancelling within 12 hours or not showing up will result in
                    a 4-day reduction in your membership.
                  </li>
                </ul>
              </div>
              <button
                className="modal-btn"
                onClick={() => navigate("/dashboard/booking")}
              >
                See you on the Court!
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default MemberGameDetails;
